import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "../components/Seo";
import { Reveal } from "../components/Motion";
import { getPaymentStatus } from "../lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", data: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", data: null });
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const data = await getPaymentStatus(sessionId);
        if (data.payment_status === "paid") {
          setState({ phase: "paid", data });
          return;
        }
        if (data.payment_status === "expired") {
          setState({ phase: "expired", data });
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts += 1;
      if (attempts < 12) timer = setTimeout(poll, 2500);
      else setState({ phase: "timeout", data: null });
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <>
      <Seo title="Payment received" description="Thank you — your payment to ProOne Solutions was received." path="/payment/success" />
      <section className="pt-36 pb-24 sm:pt-44" data-testid="payment-success-page">
        <div className="mx-auto max-w-2xl px-4 text-center">
          {state.phase === "checking" && (
            <Reveal>
              <Loader2 size={44} className="mx-auto animate-spin text-teal" aria-hidden="true" />
              <h1 className="mt-6 font-serif text-3xl sm:text-4xl font-semibold text-charcoal">Confirming your payment…</h1>
              <p className="mt-3 text-sm text-charcoal-muted">One moment while Stripe confirms the transaction.</p>
            </Reveal>
          )}
          {state.phase === "paid" && (
            <Reveal>
              <CheckCircle2 size={52} className="mx-auto text-teal" aria-hidden="true" />
              <h1 className="mt-6 font-serif text-3xl sm:text-4xl font-semibold text-charcoal" data-testid="payment-confirmed-heading">
                Thank you — received.
              </h1>
              <p className="mt-4 text-base leading-relaxed text-charcoal-muted" data-testid="payment-confirmed-detail">
                {state.data?.kind === "donation"
                  ? `Your gift of $${state.data.amount?.toFixed(2)} (${(state.data.designation || "").replace(/_/g, " ")}) is confirmed. It will go to work quietly and be reported on honestly.`
                  : `Your payment of $${state.data?.amount?.toFixed(2)} is confirmed. A receipt is on its way to your inbox.`}
              </p>
              <div className="mt-8 flex justify-center gap-3">
                <Link to="/" className="btn-primary" data-testid="success-home-btn">Back to home</Link>
                <Link to="/our-work" className="btn-outline" data-testid="success-impact-btn">See the work</Link>
              </div>
            </Reveal>
          )}
          {(state.phase === "timeout" || state.phase === "expired" || state.phase === "error") && (
            <Reveal>
              <XCircle size={48} className="mx-auto text-charcoal/40" aria-hidden="true" />
              <h1 className="mt-6 font-serif text-3xl sm:text-4xl font-semibold text-charcoal">
                {state.phase === "expired" ? "This checkout session expired." : "We couldn't confirm that just yet."}
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-charcoal-muted">
                If money moved, it is safe — write to us through the contact page and we
                will reconcile it personally.
              </p>
              <div className="mt-8 flex justify-center gap-3">
                <Link to="/support" className="btn-primary" data-testid="success-retry-btn">Back to Support / Pay</Link>
                <Link to="/contact" className="btn-outline" data-testid="success-contact-btn">Contact us</Link>
              </div>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
}

export function PaymentCancel() {
  return (
    <>
      <Seo title="Checkout cancelled" description="No charge was made. You can return to Support / Pay whenever you are ready." path="/payment/cancel" />
      <section className="pt-36 pb-24 sm:pt-44" data-testid="payment-cancel-page">
        <div className="mx-auto max-w-2xl px-4 text-center">
          <Reveal>
            <XCircle size={48} className="mx-auto text-charcoal/40" aria-hidden="true" />
            <h1 className="mt-6 font-serif text-3xl sm:text-4xl font-semibold text-charcoal">No charge was made.</h1>
            <p className="mt-4 text-sm sm:text-base leading-relaxed text-charcoal-muted">
              The checkout was cancelled before payment. Whenever you are ready, the
              door is open — no forms lost, nothing to undo.
            </p>
            <div className="mt-8 flex justify-center gap-3">
              <Link to="/support?tab=donate" className="btn-primary" data-testid="cancel-donate-btn">Return to giving</Link>
              <Link to="/" className="btn-outline" data-testid="cancel-home-btn">Back to home</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
